import { ApolloClient, ApolloLink, split } from '@apollo/client';
import { WebSocketLink } from '@apollo/client/link/ws';
import { InMemoryCache } from '@apollo/client/cache';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
// @ts-expect-error
import { createUploadLink } from 'apollo-upload-client';
import { captureException } from '@sentry/react';
import { toast } from 'react-toastify';
import { getMainDefinition, offsetLimitPagination } from '@apollo/client/utilities';
import { JWT_TOKEN_NAME } from '../constants';
import { decodeToken, getToken } from '../utils/hooks/auth';
import { buildCacheKeys, buildFromToCacheKeys } from './cacheUtils';

let host: string;
let httpProtocol: string;
let wsProtocol: string;

switch (process.env.REACT_APP_HOST_ENV) {
    case 'demo':
        host = 'dev.immerly.com/graphql';
        httpProtocol = 'https://';
        wsProtocol = 'wss://';
        break;
    case 'production':
        host = 'prod.immerly.com/graphql';
        httpProtocol = 'https://';
        wsProtocol = 'wss://';
        break;
    case 'development':
    default:
        host = 'localhost:4000/graphql';
        httpProtocol = 'http://';
        wsProtocol = 'ws://';
}

const uri = `${httpProtocol}${host}`;
const wsUri = `${wsProtocol}${host}`;

const httpLink = createUploadLink({
    uri,
}) as unknown as ApolloLink;

const wsLink = new WebSocketLink({
    uri: wsUri,
    options: {
        reconnect: true,
        connectionParams: () => {
            const token = getToken();

            return {
                Authorization: token ? `Bearer ${token}` : '',
            };
        },
    },
});

const splitLink = split(
    ({ query }) => {
        const definition = getMainDefinition(query);
        return (definition.kind === 'OperationDefinition' && definition.operation === 'subscription');
    },
    wsLink,
    httpLink,
);

const errorLink = onError(({ graphQLErrors, networkError, operation, response }) => {
    if (graphQLErrors) {
        graphQLErrors.forEach(({ stack, message }) => {
            const token = getToken();
            const decodedToken = token ? decodeToken(token) : undefined;

            toast(message, { type: 'error' });
            captureException(new Error(message),
                {
                    tags: { source: 'graphql-error-link' },
                    extra: {
                        user: decodedToken,
                        operationName: operation.operationName,
                        variables: JSON.stringify(operation.variables),
                        stack,
                        response: JSON.stringify(response),
                    }
                },
            );
            if (message.includes('Unauthorized')) {
                // add logic for logout
            }
        });
    }
    if (networkError) {
        toast(`[NETWORK ERROR]: ${networkError.message}`, { type: 'warning' });
    }
});

const authLink = setContext((_, { headers }) => {
    // get the authentication token from local storage if it exists
    const token = localStorage.getItem(JWT_TOKEN_NAME);
    // return the headers to the context so httpLink can read them
    return {
        headers: {
            ...headers,
            Authorization: token ? `Bearer ${token}` : '',
        }
    };
});

const cache = new InMemoryCache({
    typePolicies: {
        Query: {
            fields: {
                orders: offsetLimitPagination(buildCacheKeys),
                bills: offsetLimitPagination(buildCacheKeys),
                venueShifts: offsetLimitPagination(buildFromToCacheKeys),
                stockCards: offsetLimitPagination(
                    ['stockCardsIds', 'warehouseIds', 'stockCardCategoryId', 'nameOrSku', 'storageUnit', 'stockCardNameSortOrder']
                ),
                suppliers: offsetLimitPagination(['multiValueSearch']),
            }
        },
    }
});

const client = new ApolloClient({
    link: errorLink.concat(authLink.concat(splitLink)),
    cache,
    defaultOptions: {
        watchQuery: {
            fetchPolicy: 'cache-and-network',
        },
    }
});

export default client;
